import { Tabs, TabsContent, TabsList, TabsTrigger } from "@components/common/tabs";
import { DictType } from "@pma/types";
import TasksKanbanBoard from "./tasks-board";
import {
  GanttChart as GanttIcon,
  Kanban,
  Table2Icon,
  Users2Icon,
  CalendarCheck2Icon,
  PlusIcon,
  CreditCard,
  Boxes,
} from "lucide-react";
import TasksTable from "./tasks-table";
import TaskCard from "./task-card";
import { Card, CardContent } from "@components/common/card";
import UsersBoard from "./users-board";
import { useAuth } from "@stores/auth-store";
import useTranslator from "@hooks/use-translator";
import GanttChart from "./gantt-chart";
import { cn } from "@pma/utils/helpers";
import { Button } from "@components/common/button";
import { useModal } from "@stores/modal-store";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { TaskCalendar } from "./tasks-calendar";
import axios from "axios";
import { SERVER_URL, TASK_BY_CURRENT_USER_ENDPOINT } from "@pma/utils/api-routes";
import { parseCookies } from "nookies";
import { ACCESS_TOKEN } from "@pma/utils/constants";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useGisTasks } from "@pma/stores/tasks-store";
import TabViewSettings from "./tab-view-settings";
import { useTabsStore } from "@pma/stores/tabs-store";
import TaskGroup from "./task-group";
import Container from "./grouped-tasks/Container";

interface Props {
  project: DictType;
  revalidate: CallableFunction;
}

const taskTabOption = [
  {
    label: "Table",
    disabled: false,
  },
  {
    label: "Kanban",
    disabled: false,
  },
  {
    label: "Users",
    disabled: false,
  },
  {
    label: "Calendar",
    disabled: false,
  },
  {
    label: "Gantt",
    disabled: false,
  },
  {
    label: "Cards",
    disabled: false,
  },
];

export const ProjectTasksTabs = ({ project, revalidate }: Props) => {
  const { translate, locale } = useTranslator();
  const { authConfig, setTabManager } = useAuth();
  const { tabs: stateTabs } = useTabsStore();
  const [{ pageIndex, pageSize }, setPagination] = useState<any>({
    pageIndex: 1,
    pageSize: 50,
  });

  const modal = useModal();
  const { setGisTasks } = useGisTasks();
  const [searchParam] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const searchString = location.search;

  const orgId = authConfig?.last_organization_id;

  const taskStateTabs = (stateTabs["tasks"] || []) as string[];

  const hasStateTabs = taskStateTabs?.length > 0;

  const accessToken = parseCookies()[ACCESS_TOKEN];
  const projectId = project?.id;

  const queryKey = searchParam.get("revalidate");

  const getProjectTasks = async ({ pageIndex, pageSize }: any) => {
    const paylaod = {
      project_id: projectId,
      show_created_by: true,
      organization_id: orgId,
      paging_start_page: pageIndex,
      paging_per_page: pageSize,
      show_deleted: false
    };
    const response = await axios.post(SERVER_URL + TASK_BY_CURRENT_USER_ENDPOINT, paylaod, {
      headers: {
        Authorization: "Bearer " + accessToken,

        "Content-Type": "application/json",
      },
    });
    return response.data;
  };

  const {
    data: projectTasks,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["get-tasks", orgId, projectId, queryKey, pageIndex, pageSize],
    queryFn: () => getProjectTasks({ pageIndex, pageSize }),
  });

  const sortedTasks = useMemo(() => {
    return (
      projectTasks?.items.sort((a: DictType, b: DictType) => {
        // sort task by date created at asc
        return a.created_at - b.created_at;
      }) || []
    );
  }, [queryKey, isLoading, searchString, projectTasks]);

  const paginationData = useMemo(() => {
    return {
      curPage: projectTasks?.curPage,
      itemsReceived: projectTasks?.itemsReceived,
      itemsTotal: projectTasks?.itemsTotal,
      nextPage: projectTasks?.nextPage,
      offset: projectTasks?.offset,
      pageTotal: projectTasks?.pageTotal,
      prevPage: projectTasks?.prevPage,
    };
  }, [isLoading, projectTasks]);

  const paramTaskId = searchParam.get("task");

  const isHebrew = locale === "he";
  const allStateTabs = authConfig?.tabs ?? {};
  const projectDetailActiveTab = allStateTabs ? allStateTabs["project-detail"] : undefined;
  const activeTabs: DictType = projectDetailActiveTab || {
    main: "tasks",
    sub: "groups",
  };


  const showTable = !!paramTaskId || activeTabs?.sub === "groups" || undefined;

  useEffect(() => {
    const newParams = new URLSearchParams(searchParam);

    // Revalidate tasks when tasks are updated
    if (queryKey === "tasks") {
      refetch();
      newParams.delete("revalidate");

      navigate(`?${newParams.toString()}`);
    }
  }, [queryKey]);

  useEffect(() => {
    if (sortedTasks) {
      // Save tasks with GIS point in state to be accessed in <GisMap />
      const taskWithGis = sortedTasks.filter((task: DictType) => task?._gis);

      setGisTasks(taskWithGis);
    }
  }, [sortedTasks?.length]);

  return (
    <Card className='overflow-x-hidden'>
      <CardContent className='p-2 md:p-4'>
        <Tabs
          defaultValue={activeTabs?.sub}
          className={cn("relative w-full max-w-[90vw] overflow-x-auto", isHebrew ? "rtl" : "ltr")}
        >
          <TabsList className='flex flex-wrap h-fit w-full items-center justify-center rounded-full bg-transparent'>
            <TabsTrigger
              value='groups'
              className={cn(
                "gap-1 rounded-none border-primary bg-transparent pt-0 text-xs data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:shadow-none",
                taskStateTabs?.includes("Table") ? "flex" : !hasStateTabs ? "flex" : "hidden",
              )}
              onClick={() => {
                setTabManager({ group: "project-detail", main: "tasks", sub: "groups" });
                navigate(pathname);
              }}
            >
              {/* <Table2Icon size={20} className='hidden md:block' />
              {translate("Table")} */}
              <Boxes size={20} className='hidden md:block' />
              {translate("Task groups")}
            </TabsTrigger>

            <TabsTrigger
              value='kanban'
              className={cn(
                "gap-1 rounded-none border-primary bg-transparent pt-0 text-xs data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:shadow-none",
                taskStateTabs?.includes("Kanban") ? "flex" : !hasStateTabs ? "flex" : "hidden",
              )}
              onClick={() => {
                setTabManager({ group: "project-detail", main: "tasks", sub: "kanban" });

                navigate(pathname);
              }}
            >
              <Kanban size={20} className='hidden md:block' />
              {translate("Kanban")}
            </TabsTrigger>
            <TabsTrigger
              value='users'
              className={cn(
                "gap-1 rounded-none border-primary bg-transparent pt-0 text-xs data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:shadow-none",
                taskStateTabs?.includes("Users") ? "flex" : !hasStateTabs ? "flex" : "hidden",
              )}
              onClick={() => {
                setTabManager({ group: "project-detail", main: "tasks", sub: "users" });
                navigate(pathname);
              }}
            >
              <Users2Icon size={20} className='hidden md:block' />
              {translate("Users")}
            </TabsTrigger>
            <TabsTrigger
              value='calendar'
              className={cn(
                "gap-1 rounded-none border-primary bg-transparent pt-0 text-xs data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:shadow-none",
                taskStateTabs?.includes("Calendar") ? "flex" : !hasStateTabs ? "flex" : "hidden",
              )}
              onClick={() => {
                setTabManager({ group: "project-detail", main: "tasks", sub: "calendar" });
                navigate(pathname);
              }}
            >
              <CalendarCheck2Icon size={20} className='hidden md:block' />
              {translate("Calendar")}
            </TabsTrigger>
            <TabsTrigger
              value='gantt'
              className={cn(
                "gap-1 rounded-none border-primary bg-transparent pt-0 text-xs data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:shadow-none",
                taskStateTabs?.includes("Gantt") ? "flex" : !hasStateTabs ? "flex" : "hidden",
              )}
              onClick={() => {
                setTabManager({ group: "project-detail", main: "tasks", sub: "gantt" });
                navigate(pathname);
              }}
            >
              <GanttIcon size={20} className='hidden md:block' />
              {translate("Gantt")}
            </TabsTrigger>

            <TabsTrigger
              value='cards'
              className={cn(
                "gap-1 rounded-none border-primary bg-transparent pt-0 text-xs data-[state=active]:border-b-2 data-[state=active]:bg-transparent data-[state=active]:shadow-none",
                taskStateTabs?.includes("Cards") ? "flex" : !hasStateTabs ? "flex" : "hidden",
              )}
              onClick={() => {
                setTabManager({ group: "project-detail", main: "tasks", sub: "cards" });
                navigate(pathname);
              }}
            >
              <CreditCard size={20} className='hidden md:block' />
              {translate("Cards")}
            </TabsTrigger>

            <TabViewSettings options={taskTabOption} context='tasks' />
          </TabsList>

          <Card className='relative mt-2 max-w-full overflow-x-auto border-none py-0 shadow-none'>
            <CardContent className='mt-0 h-auto min-h-[50vh] w-full overflow-x-hidden p-0'>
              {/* Tasks Table */}
              <TabsContent value='table' forceMount={showTable} className={cn("p-0")}>
                {/* <TasksTable
                  tasks={sortedTasks}
                  setPagination={setPagination}
                  paginationData={paginationData}
                  isLoading={isLoading}
                /> */}
                <Container />
              </TabsContent>
              {/* Kanban Board */}

              <TabsContent
                value='kanban'
                className={cn("overflow-x-auto p-0", paramTaskId && "hidden")}
              >
                <TasksKanbanBoard tasks={sortedTasks} />
              </TabsContent>

              {/* Users Board */}

              <TabsContent
                value='users'
                className={cn("overflow-x-auto p-0", paramTaskId && "hidden")}
              >
                <UsersBoard tasks={sortedTasks} />
              </TabsContent>

              {/* Gannt Chart */}

              <TabsContent value='gantt' className={cn("p-0", paramTaskId && "hidden")}>
                <GanttChart tasks={sortedTasks} revalidate={revalidate} />
              </TabsContent>

              {/* Calendar */}

              <TabsContent value='calendar' className={cn("p-0", paramTaskId && "hidden")}>
                <TaskCalendar tasks={sortedTasks} />
              </TabsContent>

              {/* Cards */}

              <TabsContent
                value='cards'
                className={cn("grid gap-3 py-2 md:grid-cols-3", paramTaskId && "hidden")}
              >
                {sortedTasks.length > 0 ? (
                  <>
                    {sortedTasks?.map((task: DictType) => <TaskCard task={task} key={task?.id} />)}
                  </>
                ) : (
                  <div className='col-span-3 flex h-44 w-full items-center justify-center'>
                    <Button
                      onClick={() => {
                        modal.onOpen();
                        modal.setContext("task");
                      }}
                      variant={"outline"}
                      className='border-1 flex h-[60px] w-[350px] min-w-[350px] items-center gap-2 rounded-lg border-primary bg-primary/20 p-4 text-primary ring-slate-300 hover:ring-2'
                    >
                      <PlusIcon />
                      {translate("Add Task")}{" "}
                    </Button>
                  </div>
                )}
              </TabsContent>

              {/* <TabsContent value='task-group' className={cn("p-0", paramTaskId && "hidden")}>
                <Container />
              </TabsContent> */}
            </CardContent>
          </Card>
        </Tabs>
      </CardContent>
    </Card>
  );
};
